import { useState, useEffect, useCallback, type ReactNode } from "react"
import { type User, type LoginResponse } from "@/types/auth"
import { AuthContext } from "./auth-context"
import { Analytics } from "@/lib/analytics"
import { setSentryUser, clearSentryUser } from "@/lib/sentry"
import hive from "@/lib/api/hive"

// Decode JWT payload to check expiry only
function getTokenExp(token: string): number | null {
	try {
		const base64Url = token.split(".")[1]
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
		const jsonPayload = decodeURIComponent(
			window
				.atob(base64)
				.split("")
				.map(function (c) {
					return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
				})
				.join(""),
		)
		const parsed = JSON.parse(jsonPayload)
		return parsed.exp ?? null
	} catch {
		return null
	}
}

export function AuthProvider({ children }: { children: ReactNode }) {
	// 1. Lazy init: read token from localStorage
	// If access token is expired but refresh token exists, we'll attempt refresh in the effect below
	const [token, setToken] = useState<string | null>(() => {
		const stored = localStorage.getItem("beediv-token")
		if (stored) {
			const exp = getTokenExp(stored)
			if (exp && exp * 1000 > Date.now()) {
				return stored
			}
			// Access token expired — check if we have a refresh token to try later
			const refreshToken = localStorage.getItem("beediv-refresh-token")
			if (refreshToken) {
				return null // Will attempt refresh in the effect
			}
			// No refresh token either — clear everything
			localStorage.removeItem("beediv-token")
			localStorage.removeItem("beediv-refresh-token")
			localStorage.removeItem("beediv-user")
		}
		return null
	})

	// Lazy init user from cache — avoids auth spinner on refresh when token + user are already known
	const [user, setUser] = useState<User | null>(() => {
		try {
			const cached = localStorage.getItem("beediv-user")
			return cached ? JSON.parse(cached) : null
		} catch {
			return null
		}
	})

	// Start loading only if we can't resolve auth optimistically from cache
	const [isLoading, setIsLoading] = useState(() => {
		const stored = localStorage.getItem("beediv-token")
		if (!stored) return false // no token = not loading, just not authed
		const exp = getTokenExp(stored)
		const hasValidToken = !!(exp && exp * 1000 > Date.now())
		const hasCachedUser = !!localStorage.getItem("beediv-user")
		return !(hasValidToken && hasCachedUser) // only loading if we can't resolve optimistically
	})

	// Keeps user state and localStorage cache in sync
	const updateUser = useCallback((u: User | null) => {
		setUser(u)
		if (u) localStorage.setItem("beediv-user", JSON.stringify(u))
		else localStorage.removeItem("beediv-user")
	}, [])

	// 2. Session restore: fetch user from API when token exists, or attempt refresh
	useEffect(() => {
		let cancelled = false

		const restoreSession = async () => {
			// If we have a valid token, use it directly
			if (token) {
				try {
					const fetchedUser = await hive.getMe(token)
					if (cancelled) return
					updateUser(fetchedUser)
					Analytics.identify(fetchedUser.id)
					setSentryUser(fetchedUser.id, fetchedUser.email)
					Analytics.setPersonProperties({
						$email: fetchedUser.email,
						$first_name: fetchedUser.first_name,
						$last_name: fetchedUser.last_name,
						$created: fetchedUser.created_at,
					})
					Analytics.capture("Session Restored")
				} catch {
					if (cancelled) return
					// getMe failed — apiFetch may have already tried refresh
					// If still failing, clear session
					localStorage.removeItem("beediv-token")
					localStorage.removeItem("beediv-refresh-token")
					setToken(null)
					updateUser(null)
				}
				if (!cancelled) setIsLoading(false)
				return
			}

			// No valid access token — try refresh if we have a refresh token
			const refreshToken = localStorage.getItem("beediv-refresh-token")
			if (!refreshToken) {
				if (!cancelled) setIsLoading(false)
				return
			}

			try {
				const response = await fetch(`https://api.beediv.com/v1/auth/refresh`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ refresh_token: refreshToken }),
				})
				const data = await response.json()

				if (!response.ok || data.error) {
					throw new Error("Refresh failed")
				}

				const tokens = data.payload as LoginResponse
				if (cancelled) return

				localStorage.setItem("beediv-token", tokens.access_token)
				localStorage.setItem("beediv-refresh-token", tokens.refresh_token)
				setToken(tokens.access_token)

				const fetchedUser = await hive.getMe(tokens.access_token)
				if (cancelled) return
				updateUser(fetchedUser)
				Analytics.identify(fetchedUser.id)
				setSentryUser(fetchedUser.id, fetchedUser.email)
				Analytics.setPersonProperties({
					$email: fetchedUser.email,
					$first_name: fetchedUser.first_name,
					$last_name: fetchedUser.last_name,
					$created: fetchedUser.created_at,
				})
				Analytics.capture("Session Restored")
			} catch {
				if (cancelled) return
				localStorage.removeItem("beediv-token")
				localStorage.removeItem("beediv-refresh-token")
				setToken(null)
				updateUser(null)
			}
			if (!cancelled) setIsLoading(false)
		}

		restoreSession()
		return () => {
			cancelled = true
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// 3. Listen for token refresh events from apiFetch
	useEffect(() => {
		const handleTokenRefreshed = (e: Event) => {
			const detail = (e as CustomEvent).detail
			setToken(detail.access_token)
		}

		const handleSessionExpired = () => {
			setToken(null)
			updateUser(null)
		}

		window.addEventListener("beediv-token-refreshed", handleTokenRefreshed)
		window.addEventListener("beediv-session-expired", handleSessionExpired)

		return () => {
			window.removeEventListener("beediv-token-refreshed", handleTokenRefreshed)
			window.removeEventListener("beediv-session-expired", handleSessionExpired)
		}
	}, [])

	// 4. Login action
	const login = useCallback(
		async (data: LoginResponse, isSignup?: boolean) => {
			localStorage.setItem("beediv-token", data.access_token)
			localStorage.setItem("beediv-refresh-token", data.refresh_token)
			setToken(data.access_token)

			const fetchedUser = await hive.getMe(data.access_token)
			updateUser(fetchedUser)

			if (isSignup) {
				// Push timezone-detected local currency to server so it persists
				const localCurrency = localStorage.getItem("beediv-currency")
				if (localCurrency && localCurrency !== "USD" && localCurrency !== fetchedUser.display_currency) {
					hive.updateMe({ display_currency: localCurrency }, data.access_token).catch(() => {})
				}
				// Push current theme to server for email rendering
				const localTheme = localStorage.getItem("beediv-theme") || "system"
				const resolvedTheme =
					localTheme === "system"
						? window.matchMedia("(prefers-color-scheme: dark)").matches
							? "dark"
							: "light"
						: localTheme
				hive.updateMe({ email_theme: resolvedTheme }, data.access_token).catch(() => {})
				Analytics.alias(fetchedUser.id)
				Analytics.capture("Signup")
			} else {
				Analytics.capture("Login")
			}
			Analytics.identify(fetchedUser.id)
			setSentryUser(fetchedUser.id, fetchedUser.email)
			Analytics.setPersonProperties({
				$email: fetchedUser.email,
				$first_name: fetchedUser.first_name,
				$last_name: fetchedUser.last_name,
				$created: fetchedUser.created_at,
			})
		},
		[updateUser],
	)

	// 5. Logout action
	const logout = useCallback(() => {
		localStorage.removeItem("beediv-token")
		localStorage.removeItem("beediv-refresh-token")
		setToken(null)
		updateUser(null)
		Analytics.capture("Logout")
		Analytics.reset()
		clearSentryUser()
	}, [updateUser])

	return (
		<AuthContext.Provider
			value={{
				user,
				token,
				isLoading,
				login,
				logout,
				isAuthenticated: !!user,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}
